import type { Session } from '@shared/sessions/types'
import type { GeoStamp } from '@shared/sessions/types'
import { fetchAuthSession } from 'aws-amplify/auth'

export type BookCallbackSessionInput = {
  providerUserId: string
  memberUserId: string
  callId: string
  scheduledStartMs?: number
  scheduledEndMs?: number
  notes?: string
  locationSnap?: GeoStamp | null
  clientRequestId?: string
}

export type BookCallbackSessionResponse = {
  session: Session
  status: 'created' | 'duplicate'
}

function getBaseUrl(): string {
  const url = import.meta.env.VITE_SESSIONS_API_URL ?? ''
  if (!url) {
    throw new Error(
      'VITE_SESSIONS_API_URL is not set. Deploy SessionsStack to book callbacks.',
    )
  }
  return url.replace(/\/$/, '')
}

async function getIdToken(): Promise<string | null> {
  const session = await fetchAuthSession()
  return session.tokens?.idToken?.toString() ?? null
}

/** POST /sessions/callback — book a provider callback from call transfer. */
export async function bookCallbackSession(
  input: BookCallbackSessionInput,
): Promise<BookCallbackSessionResponse> {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')

  const res = await fetch(`${getBaseUrl()}/sessions/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(input),
  })

  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(callbackErrorMessage(res.status, detail))
  }

  return (await res.json()) as BookCallbackSessionResponse
}

const CALLBACK_ERROR_COPY: Record<string, string> = {
  RATE_LIMITED: 'Too many callbacks booked in the last hour — try again later.',
  ALREADY_BOOKED: 'A callback is already booked for this person.',
  NOT_ON_CALL: 'Callbacks can only be booked for a call you were on.',
  MEMBER_NOT_ON_CALL: 'Callbacks can only be booked for someone on this call.',
  PROVIDER_NOT_ELIGIBLE: 'That person can’t take callbacks.',
}

export function callbackErrorMessage(status: number, detail: string): string {
  let code: string | undefined
  try {
    code = (JSON.parse(detail) as { error?: string }).error
  } catch {
    code = undefined
  }
  return (code && CALLBACK_ERROR_COPY[code]) || `Could not book callback session (${status}).`
}
